/**
 * The task drawer's notes log (DD-050): team only, newest first, each with
 * its author and a relative time (the full date on hover), the body as
 * markdown. Typed notes are written in the block editor and saved as
 * markdown (TD-092). The mic swaps in Home's VoiceRecorder, and a sent
 * clip shows as a pending row above the log until its note lands
 * (TD-089, DD-046). A recording held on the create form arrives as
 * `firstNote` and is sent here, so it gets the same row.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MicIcon, SpinnerIcon } from "@/components/icons";
import { NoteDisplay } from "@/components/NoteCard";
import { Button } from "@/components/ui/Button";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useAppStore } from "@/core/store/useAppStore";
import { VoiceClipRow, VoiceRecorder, type VoiceRecording, useVoiceClips } from "@/core/voice";
import { useAddTaskNoteMutation, useTaskNotesQuery } from "../hooks/useTasks";
import { taskKeys } from "../types";
import { canAddNote, hasUnsentVoice, noteAuthorLabel, noteFullTime, noteRelativeTime } from "./taskNotes";

interface TaskNotesSectionProps {
  taskId: string;
  /** True while a recording runs or waits unsent. The drawer asks before
   * closing on it. */
  onUnsentChange: (hasUnsent: boolean) => void;
  /** The recording held while the task was created, to send as its first note. */
  firstNote?: VoiceRecording | null;
  /** Called once firstNote is sent, so the drawer drops it. */
  onFirstNoteTaken?: () => void;
}

const SECTION_LABEL = "text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em]";

export function TaskNotesSection({ taskId, onUnsentChange, firstNote, onFirstNoteTaken }: TaskNotesSectionProps) {
  const queryClient = useQueryClient();
  const addToast = useAppStore((state) => state.addToast);
  const { data: notes, isLoading, isError } = useTaskNotesQuery(taskId);
  const addNote = useAddTaskNoteMutation(taskId);
  const [body, setBody] = useState("");
  // The editor reads its content once on mount: a new key empties it after a save.
  const [editorKey, setEditorKey] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recorderUnsent, setRecorderUnsent] = useState(false);
  const [confirmingWrite, setConfirmingWrite] = useState(false);

  // Memoized: useVoiceClips keys its list and its polling on the target.
  const voiceTarget = useMemo(() => ({ task_id: taskId }), [taskId]);
  const refreshNotes = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: taskKeys.notes(taskId) });
  }, [queryClient, taskId]);
  const voice = useVoiceClips({ purpose: "task_note", target: voiceTarget, onCompleted: refreshNotes });

  // Exactly once: the ref outlives StrictMode's second effect run, and the
  // drawer drops firstNote, so a real remount has nothing left to send.
  const sentFirstNoteRef = useRef<VoiceRecording | null>(null);
  const { send } = voice;
  useEffect(() => {
    if (!firstNote || sentFirstNoteRef.current === firstNote) return;
    sentFirstNoteRef.current = firstNote;
    send(firstNote.blob, firstNote.mimeType, firstNote.durationSeconds);
    onFirstNoteTaken?.();
  }, [firstNote, send, onFirstNoteTaken]);

  // The drawer asks before closing on an unsent recording or a failed upload.
  const unsent = hasUnsentVoice(recorderUnsent, voice.clips);
  useEffect(() => {
    onUnsentChange(unsent);
    return () => onUnsentChange(false);
  }, [unsent, onUnsentChange]);

  const handleWriteInstead = () => {
    if (recorderUnsent) setConfirmingWrite(true);
    else setRecording(false);
  };

  // A failed save keeps the text in the box (spec, Error handling).
  const handleSave = () => {
    if (!canAddNote(body)) return;
    addNote.mutate(body.trim(), {
      onSuccess: () => {
        setBody("");
        setEditorKey((key) => key + 1);
      },
      onError: () => addToast({ message: "Impossibile salvare la nota.", type: "error" }),
    });
  };

  const handleSendVoice = (blob: Blob, mimeType: string, durationSeconds: number) => {
    voice.send(blob, mimeType, durationSeconds);
    setConfirmingWrite(false);
    setRecording(false);
  };

  let log: React.ReactNode;
  if (isLoading) {
    log = (
      <div className="flex justify-center py-3">
        <SpinnerIcon className="w-5 h-5 text-gray-400" />
      </div>
    );
  } else if (isError) {
    log = <p className="text-sm text-red-600">Impossibile caricare le note.</p>;
  } else if (!notes || notes.length === 0) {
    log = <p className="text-sm text-gray-400">Nessuna nota.</p>;
  } else {
    const now = Date.now();
    log = (
      <ul className="divide-y divide-gray-100">
        {notes.map((note) => (
          <li key={note.id} className="py-3">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="truncate font-medium text-gray-700">{noteAuthorLabel(note.author)}</span>
              <time dateTime={note.createdAt} title={noteFullTime(note.createdAt)} className="shrink-0 text-gray-400">
                {noteRelativeTime(note.createdAt, now)}
              </time>
            </div>
            <NoteDisplay content={note.body} className="mt-1" />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section>
      <h3 className={`${SECTION_LABEL} mb-2`}>Note</h3>
      {recording ? (
        <div className="space-y-3">
          <VoiceRecorder onSend={handleSendVoice} onUnsentChange={setRecorderUnsent} />
          {confirmingWrite ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-700">Scartare la registrazione non inviata?</span>
              <button
                type="button"
                onClick={() => setConfirmingWrite(false)}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmingWrite(false);
                  setRecording(false);
                }}
                className="text-sm font-medium text-red-600 hover:text-red-700"
              >
                Scarta
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleWriteInstead}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Scrivi invece
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Remounts after a save and after a trip to the recorder: start from the draft. */}
          <RichTextEditor
            key={editorKey}
            format="markdown"
            initialContent={body}
            onContentChange={setBody}
            placeholder="Aggiungi una nota…"
            className="min-h-[6rem] rounded-md border border-gray-300 py-2"
          />
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setRecording(true)}
              title="Registra una nota vocale"
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <MicIcon className="w-4 h-4" />
              Registra
            </button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!canAddNote(body)}
              isLoading={addNote.isPending}
            >
              Salva
            </Button>
          </div>
        </div>
      )}
      {voice.clips.length > 0 && (
        <div className="mt-3 rounded-lg border border-gray-100">
          {voice.clips.map((view) => (
            <VoiceClipRow
              key={view.key}
              view={view}
              confirmInline
              onRetry={(clipId) => void voice.retry(clipId)}
              onDiscard={(clipId) => void voice.discard(clipId)}
              onRetryUpload={voice.retryUpload}
              onDiscardUpload={voice.discardUpload}
            />
          ))}
        </div>
      )}
      <div className="mt-3">{log}</div>
    </section>
  );
}
